import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { RegionsListFilter } from './regions-list-filter/regions-list-filter';
import {
  RegionFilters,
  RegionListItem,
} from '../../../../pages/admin-dashboard/region-view-list/list-of-regions/list-of-regions.model';
import { RegionsListService } from '../../../../services/Regions/regions-list';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { AddIcon } from '../../../../shared/ui/icons/add-icon/add-icon';
import { RegionsDeleteService } from '../../../../services/Regions/regions-delete';
import { DELETE_REGION_ERROR_CODES } from '../regions-delete/delete-regions.model';
import { ApiErrorResponse } from '../../../../pages/admin-dashboard/return-of-requests-list/return-of-requests-list.model';
import { Loading } from '../../../../utils/loading/loading';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { mapRegionsToTableRows } from './utils/rgions-list.mapper';

@Component({
  selector: 'app-regions-list',
  standalone: true,
  imports: [
    Tables,
    Pagination,
    ConfirmationDialog,
    SuccessfulNotification,
    RegionsListFilter,
    AddIcon,
    Loading,
    ErrorData,
  ],
  templateUrl: './regions-list.html',
})
export class RegionsList implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private listService = inject(RegionsListService);
  private deleteService = inject(RegionsDeleteService);

  isArabic = computed(() => this.locale.startsWith('ar'));

  initialCityId = signal<number | null>(null);

  filters = signal<RegionFilters>({
    pageNum: 0,
    pageSize: 10,
    orderBy: 'CREATION_DATE',
    orderDir: 'DESC',
  });

  totalItems = signal(0);
  loading = signal(true);
  errorMsg = signal<string | null>(null);

  regionsData = signal<any[]>([]);

  tableHeaders = computed(() => [
    { key: 'title', label: this.isArabic() ? 'العنوان' : 'Title' },
    { key: 'intervals', label: this.isArabic() ? 'عدد الفترات' : 'Intervals' },
    {
      key: 'createdOnBy',
      label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created Date / By',
      type: 'createdOnBy',
    },
    {
      key: 'lastModifiedOnBy',
      label: this.isArabic() ? 'آخر تعديل / بواسطة' : 'Last Modified / By',
      type: 'lastModifiedOnBy',
    },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  tableActions = computed(() => [
    { id: 1, titleAr: 'عرض تفاصيل المنطقة', titleEn: 'View Region Details', icon: 'view' },
    { id: 2, titleAr: 'تعديل', titleEn: 'Edit', icon: 'edit' },
    { id: 3, titleAr: 'حذف', titleEn: 'Delete', icon: 'delete' },
  ]);

  isDeleteDialogOpen = signal(false);
  itemToDelete = signal<RegionListItem | null>(null);
  deleteLoading = signal(false);
  deleteError = signal<string>('');
  isInUseError = signal(false);

  showSuccessMessage = signal(false);

  readonly currentPage = computed(() => this.filters().pageNum + 1);

  readonly totalPages = computed(() => Math.ceil(this.totalItems() / this.filters().pageSize) || 1);

  readonly startItem = computed(() =>
    this.totalItems() === 0 ? 0 : this.filters().pageNum * this.filters().pageSize + 1,
  );

  readonly endItem = computed(() => Math.min(this.startItem() + this.filters().pageSize - 1, this.totalItems()));

  ngOnInit(): void {
    const cityIdParam =
      this.route.snapshot.paramMap.get('cityId') ||
      this.route.snapshot.paramMap.get('id') ||
      this.route.parent?.snapshot.paramMap.get('cityId');

    if (cityIdParam) {
      const cityId = Number(cityIdParam);
      this.initialCityId.set(cityId);
      this.filters.update((f) => ({
        ...f,
        cityId,
        pageNum: 0,
      }));
    }

    this.loadRegions();
  }

  loadRegions(): void {
    this.loading.set(true);
    this.errorMsg.set(null);

    this.listService
      .getAllRegions(this.filters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.totalItems.set(res.total);
          const mappedRows = mapRegionsToTableRows(res.list || [], this.isArabic());
          this.regionsData.set(mappedRows);
          this.loading.set(false);
        },
        error: () => {
          this.errorMsg.set(this.isArabic() ? 'حدث خطأ أثناء جلب البيانات' : 'Error fetching data');
          this.loading.set(false);
        },
      });
  }

  onActionTriggered(event: { event: Event; actionId: number; entity: any }) {
    const region = event.entity.rawEntity || event.entity;

    if (event.actionId === 1) {
      this.router.navigate(['/admin/view-region-details', region.id]);
    } else if (event.actionId === 2) {
      this.router.navigate(['/admin/edit-region', region.id]);
    } else if (event.actionId === 3) {
      this.openDeleteDialog(region);
    }
  }

  onApplyFilters(newFilters: Partial<RegionFilters>) {
    this.filters.update((f) => ({ ...f, ...newFilters, pageNum: 0 }));
    this.loadRegions();
  }

  onResetFilters() {
    const cityId = this.initialCityId();
    this.filters.set({
      pageNum: 0,
      pageSize: 10,
      orderBy: 'CREATION_DATE',
      orderDir: 'DESC',
      cityId: cityId ?? undefined,
    });
    this.loadRegions();
  }

  // isFiltersApplied(): boolean {
  //   const f = this.filters();
  //   return !!(f.quickSearch || f.governorateId || f.createdOnFrom || f.lastModifiedOnFrom);
  // }
  openDeleteDialog(region: RegionListItem) {
    this.itemToDelete.set(region);
    this.deleteError.set('');
    this.isInUseError.set(false);
    this.isDeleteDialogOpen.set(true);
  }

  closeDeleteDialog() {
    this.isDeleteDialogOpen.set(false);
    this.itemToDelete.set(null);
    this.deleteError.set('');
    this.isInUseError.set(false);
    this.deleteLoading.set(false);
  }

  onDeleteDialogSubmit() {
    if (this.isInUseError()) {
      this.closeDeleteDialog();
      return;
    }

    const id = this.itemToDelete()?.id;
    if (!id || this.deleteLoading()) return;

    this.deleteLoading.set(true);
    this.deleteError.set('');

    this.deleteService
      .deleteRegion(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const deletedName = this.isArabic() ? this.itemToDelete()?.titleAr : this.itemToDelete()?.titleEn;

          this.deleteLoading.set(false);
          this.closeDeleteDialog();
          this.showSuccessMessage.set(true);

          this.regionsData.update((currentRows) => currentRows.filter((row) => row.id !== id));
          this.totalItems.update((total) => Math.max(0, total - 1));

          setTimeout(() => this.showSuccessMessage.set(false), 3000);
        },
        error: (err: ApiErrorResponse) => {
          this.deleteLoading.set(false);

          if (
            err?.code === DELETE_REGION_ERROR_CODES.REGION_IN_USE ||
            err?.code === DELETE_REGION_ERROR_CODES.REGION_IN_USE_PHARMACY ||
            err?.code === DELETE_REGION_ERROR_CODES.REGION_IN_USE_ROUTE
          ) {
            this.isInUseError.set(true);
            this.deleteError.set('');
          } else {
            this.deleteError.set(
              this.isArabic()
                ? err?.messageAr || 'حدث خطأ أثناء حذف المنطقة'
                : err?.messageEn || 'Error deleting region',
            );
          }
        },
      });
  }

  navigateToAdd() {
    const cityId = this.initialCityId();
    if (cityId) {
      this.router.navigate(['/admin/add-region'], { queryParams: { cityId } });
    } else {
      this.router.navigate(['/admin/add-region']);
    }
  }

  // onApplyFilters(newFilters: Partial<RegionFilters>) {
  //   this.filters.update((f) => ({ ...f, ...newFilters, pageNum: 0 }));
  //   this.loadRegions();
  // }

  // onResetFilters() {
  //   this.filters.set({
  //     pageNum: 0,
  //     pageSize: 10,
  //     orderBy: 'CREATION_DATE',
  //     orderDir: 'DESC',
  //   });
  //   this.loadRegions();
  // }

  isFiltersApplied(): boolean {
    const f = this.filters();
    return !!(f.quickSearch || f.governorateId || f.cityId || f.createdOnFrom || f.lastModifiedOnFrom);
  }

  // get currentPage() {
  //   return this.filters().pageNum + 1;
  // }

  // get totalPages() {
  //   return Math.ceil(this.totalItems() / this.filters().pageSize) || 1;
  // }

  // get startItem() {
  //   return this.totalItems() === 0 ? 0 : this.filters().pageNum * this.filters().pageSize + 1;
  // }

  // get endItem() {
  //   return Math.min(this.startItem + this.filters().pageSize - 1, this.totalItems());
  // }

  getPageNumbers(): number[] {
    return Array.from({ length: this.totalPages() }, (_, i) => i + 1);
  }

  goToPage(page: number) {
    this.filters.update((f) => ({ ...f, pageNum: page - 1 }));
    this.loadRegions();
  }

  nextPage() {
    if (this.currentPage() < this.totalPages()) this.goToPage(this.currentPage() + 1);
  }

  previousPage() {
    if (this.currentPage() > 1) this.goToPage(this.currentPage() - 1);
  }

  onPageSizeChange(size: number) {
    this.filters.update((f) => ({ ...f, pageSize: size, pageNum: 0 }));
    this.loadRegions();
  }
}
